import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getSessionItem } from '@atlassian/search-dialog/session-storage-utils';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { AIAnswerQueryStreamType } from '../ai-answer-dialog/ai-answer-query-stream-type';
import { NLPSearchErrorCodes } from '../ai-answer-dialog/nlp-search-error-codes';
import { NLPSearchErrorState } from '../ai-answer-dialog/nlp-search-error-state';
import { NLPSearchResultFormat } from '../ai-answer-dialog/nlp-search-result-format';
import type {
	AIAnswerQueryStream,
	AIAnswerQueryStreamEntry,
	AIAnswerQueryStreamMarkdownFinalPart,
	NLPSearchResultSource,
	NLPSearchType,
	SearchAIAnswerQueryType,
	SearchAIAnswerQueryVariables,
} from '../ai-answer-dialog/types';
import { logger } from '../common/utils/logger';
import { SAINFeatureControl } from '../common/utils/sain-feature-control';
import {
	ASSISTANCE_SERVICE_API_BASE_URL,
	PROMPT_OVERRIDE_SESSION_KEY,
	SAIN_STREAMING_API,
} from '../constants';

import { extractStructuredErrorDetails } from './extract-structured-error-details';
import { type AgentError, type AgentResponse, type StreamError } from './types';
import { mapStreamDefinitionResponse, mapStreamSAINResponse } from './utils';

const identityUserCloudARIPrefix = 'ari:cloud:identity::user/';

// Response headers that may carry a trace id we can use to correlate a failed
// request with backend/gateway (e.g. Stargate) logs. Checked in priority order.
const TRACE_ID_HEADERS = ['atl-traceid', 'x-b3-traceid', 'x-trace-id', 'x-amzn-trace-id'];

// Fallback used when we cannot (or are not allowed to) derive structured error
// details from the backend response.
const DEFAULT_BACKEND_ERROR_DETAILS = 'Unhandled error response received';

/**
 * Best-effort extraction of a trace id from the backend response headers so
 * that a failed request can be correlated with backend/gateway logs.
 */
function getTraceIdFromResponse(response: Response): string | undefined {
	try {
		for (const header of TRACE_ID_HEADERS) {
			const value = response.headers?.get?.(header);
			if (value) {
				return value;
			}
		}
	} catch {
		// Accessing headers should never throw, but guard defensively so error
		// handling never fails because of trace-id extraction.
	}
	return undefined;
}

const EXPECTED_STREAM_CONTENT_TYPES = [
	'application/json',
	'application/x-ndjson',
	'application/ndjson',
	'text/event-stream',
];

function getResponseContentType(response: Response): 'expected' | 'missing' | 'unexpected' {
	const contentType = response.headers?.get?.('content-type');
	if (!contentType) {
		return 'missing';
	}

	return EXPECTED_STREAM_CONTENT_TYPES.some((expectedContentType) =>
		contentType.toLowerCase().startsWith(expectedContentType),
	)
		? 'expected'
		: 'unexpected';
}

/**
 * Best-effort read of the backend error response body, returning only
 * structured, allow-listed (non-UGC) details. Never throws.
 */
async function getStructuredErrorDetailsFromResponse(
	response: Response,
): Promise<string | undefined> {
	try {
		return extractStructuredErrorDetails(await response.text());
	} catch {
		return undefined;
	}
}

type BackendErrorResponse = {
	state: 'failed';
	reason: 'backend';
	details: string;
	errorCode: number;
	backendFailureType: 'hipaa' | 'http_error';
	traceId?: string;
};

const getNonOkResponseResult = async (
	response: Response,
	traceId: string | null,
): Promise<BackendErrorResponse> => {
	let responseBody = '';
	try {
		responseBody = await response.text();
	} catch {
		// Preserve the HTTP status even when the error body cannot be read.
	}

	if (responseBody.includes(NLPSearchErrorCodes.HIPAA_ENABLED_ERROR)) {
		return {
			state: 'failed',
			reason: 'backend',
			details: NLPSearchErrorCodes.HIPAA_ENABLED_ERROR,
			errorCode: response.status,
			backendFailureType: 'hipaa',
		};
	}

	return {
		state: 'failed',
		reason: 'backend',
		details: fg('kd_sain_capture_backend_error_details')
			? (extractStructuredErrorDetails(responseBody) ?? DEFAULT_BACKEND_ERROR_DETAILS)
			: DEFAULT_BACKEND_ERROR_DETAILS,
		errorCode: response.status,
		backendFailureType: 'http_error',
		traceId: traceId ?? undefined,
	};
};

/**
 * Build a non-UGC diagnostic `details` string for the "stream ended without a
 * valid FINAL_RESPONSE" case. Records what the backend actually sent as its last
 * message (the `type` discriminator) and, when the last message is an error
 * envelope, its allow-listed error key/status. Deliberately avoids recording any
 * free-form message text so no user-generated content is captured.
 */
function getNoFinalResponseDetails(lastMessage: { type?: unknown } | undefined): string {
	const receivedType =
		lastMessage && typeof lastMessage.type === 'string' ? lastMessage.type : 'undefined';
	let structured: string | undefined;
	try {
		structured = extractStructuredErrorDetails(JSON.stringify(lastMessage));
	} catch {
		structured = undefined;
	}
	return structured
		? `No final response; received type: ${receivedType}; ${structured}`
		: `No final response; received type: ${receivedType}`;
}

function getAgentAndExperience(isReadingAids: boolean) {
	return isReadingAids
		? {
				named_id: 'reading_aids_agent',
				experience: 'reading-aids',
			}
		: {
				named_id: 'sain_agent',
				experience: 'sain',
			};
}

function mapMimeTypeToFormat(mimeType: string): NLPSearchResultFormat {
	switch (mimeType) {
		case 'text/markdown':
			return NLPSearchResultFormat.MARKDOWN;
		case 'text/adf':
			return NLPSearchResultFormat.ADF;
		case 'text/json':
			return NLPSearchResultFormat.JSON;
		default:
			return NLPSearchResultFormat.MARKDOWN;
	}
}

type MappedError = {
	errorState: NLPSearchErrorState;
	networkErrorReason?: string;
	networkErrorDetails?: string;
	backendFailureType?: string;
};

function mapErrorToSain(error: StreamError | AgentError, traceId?: string | null): MappedError {
	const errorMessage =
		'message_template' in error ? error.message_template : error.message.message_template;

	switch (errorMessage) {
		case 'NO_ANSWER_LLM':
			return { errorState: NLPSearchErrorState.NoAnswer };
		case 'NO_ANSWER_PEOPLE':
			return { errorState: NLPSearchErrorState.NoAnswerWhoQuestion };
		case 'NO_ANSWER_KEYWORDS':
			return { errorState: NLPSearchErrorState.NoAnswerKeywords };
		case 'NO_ANSWER_SEARCH_QA_PLUGIN':
			return { errorState: NLPSearchErrorState.NoAnswerSearchResults };
		case 'ACCEPTABLE_USE_VIOLATIONS':
			return { errorState: NLPSearchErrorState.AcceptableUseViolation };
		case 'OPENAI_RATE_LIMIT_USER_ABUSE':
			return { errorState: NLPSearchErrorState.NoAnswerOpenAIRateLimitUserAbuse };
		case 'FEATURE_DISABLED_ON_SITE':
			return { errorState: NLPSearchErrorState.AIDisabled };
		default:
			logger.error({
				error,
				functionName: 'mapErrorToSain: NetworkError',
				traceId,
			});
			return {
				errorState: NLPSearchErrorState.NetworkError,
				networkErrorReason: 'unmapped_backend_error',
				networkErrorDetails: errorMessage,
				backendFailureType: 'unmapped_template',
			};
	}
}

function mapMessageToNLPSearchType(
	message: AgentResponse,
	requestId?: string | null,
): NLPSearchType {
	// Handle cases where message is directly in root or nested under message.message
	const messageContent = message.message || message;
	return {
		nlpResult: messageContent.content,
		uniqueSources: message.sources
			? message.sources.map(
					(source): NLPSearchResultSource => ({
						ari: source.ari,
						id: source.id?.toString(),
						title: source.title,
						product: source.product,
						type: source.type,
						url: source.url,
						lastModified: source.lastModified,
						iconUrl: null,
						spaceName: null,
						spaceUrl: null,
					}),
				)
			: [],
		disclaimer: messageContent.message_metadata?.disclaimer ?? null,
		errorState: null,
		format: mapMimeTypeToFormat(messageContent.content_mime_type),
		extraAPIAnalyticsAttributes: {
			message_id: messageContent.id,
			conversational_channel_id: messageContent.conversation_channel_id,
			experience_id: messageContent.experience_id,
			user_id: messageContent.user_ari?.replace(identityUserCloudARIPrefix, '') ?? null,
			userQuery: message.human_message?.content ?? null,
			requestId: fg('kd_fix_sain_mau_instrumentation')
				? (requestId ?? null)
				: (message.message?.message_metadata?.request_id ?? null),
			answerGenerationModel: message.usage_data ?? null,
			...(!fg('kd_consolidate_sources_analytics') && { sources: message.sources }),
			cacheInfo: message.message?.message_metadata?.global_cache_info ?? null,
			pluginInvocations: message.message?.plugin_invocations,
			rawJson: message,
			...(fg('kd_sain_online_debugger') && {
				minion_outputs: message.message?.minion_outputs,
				traceId: requestId ?? null,
			}),
		},
		authority_ui_metadata: messageContent.authority_ui_metadata ?? null,
	};
}

export type FetchConfig = {
	hostName?: string;
	credentials?: 'omit' | 'same-origin' | 'include';
	entity_ari?: string;
	headers?: { [key: string]: string };
	timeout?: number;
};

type AskSainFetchHandlerArgs = {
	fetchReturn?: Promise<Response>;
	variables?: SearchAIAnswerQueryVariables;
	fetchConfig?: FetchConfig;
};

export async function* sainStreamFetchHandler({
	fetchReturn,
	variables,
	fetchConfig,
}: AskSainFetchHandlerArgs): AIAnswerQueryStream {
	try {
		const { query, locale, experience, filters, cloudIdARI, followUpsEnabled } = variables || {};
		const url = `${fetchConfig?.hostName || ''}${SAIN_STREAMING_API}`;
		const response = await (fetchReturn
			? fetchReturn
			: fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						query,
						filters,
						locations: [cloudIdARI],
						locale,
						experience,
						followUpsEnabled,
					}),
					credentials: fetchConfig?.credentials || 'same-origin', // fetch() uses 'same-origin' by default
				}));

		if (!response.ok) {
			// For non 200 status codes. When enabled, record structured, non-UGC
			// details (error key/status) and a trace id so upstream failures (e.g.
			// Stargate 404 FAILURE_CLIENT_TCS) are diagnosable. The full body stays
			// retrievable from backend/gateway logs via the trace id.
			const captureBackendErrorDetails = fg('kd_sain_capture_backend_error_details');
			const traceId = captureBackendErrorDetails ? getTraceIdFromResponse(response) : undefined;
			const details = captureBackendErrorDetails
				? ((await getStructuredErrorDetailsFromResponse(response)) ?? DEFAULT_BACKEND_ERROR_DETAILS)
				: DEFAULT_BACKEND_ERROR_DETAILS;
			return {
				state: 'failed',
				reason: 'backend',
				details,
				errorCode: response.status,
				traceId,
			};
		}

		if (!response.body) {
			return {
				state: 'failed',
				reason: 'backend',
				details: 'response.body missing',
				errorCode: response.status,
				traceId: fg('kd_sain_capture_backend_error_details')
					? getTraceIdFromResponse(response)
					: undefined,
			};
		}

		try {
			const reader = response.body.getReader();
			const decoder = new TextDecoder('utf-8');
			let buffer = '';
			let done = false;

			let finalResponse: AIAnswerQueryStreamMarkdownFinalPart['value'] | undefined;

			while (!done) {
				let readResult: ReadableStreamReadResult<Uint8Array>;
				try {
					readResult = await reader.read();
				} catch (error: unknown) {
					if (fg('kd_sain_stream_error_diagnostics')) {
						return {
							state: 'failed',
							reason: 'error',
							details: error instanceof Error ? error.message : 'Unable to read stream',
							diagnosticType: 'unexpected_stream_error',
							failureStage: 'stream_read',
							responseContentType: getResponseContentType(response),
							traceId: getTraceIdFromResponse(response),
							error: error instanceof Error ? error : new Error('Unable to read stream'),
						};
					}

					return { state: 'failed', reason: 'parsing', error };
				}
				const { value, done: doneReading } = readResult;
				done = doneReading;
				const chunkValue = decoder.decode(value);
				buffer = buffer + chunkValue;

				// Split the buffer by line breaks
				const lines = buffer.split('\n');
				// Process all complete lines, except for the last one (which might be incomplete)
				while (lines.length > 1) {
					const line = lines.shift()!;
					const parsedData = JSON.parse(line) as
						| AIAnswerQueryStreamEntry
						| AIAnswerQueryStreamMarkdownFinalPart['value'];

					if (
						parsedData.type === AIAnswerQueryStreamType.AnswerType ||
						parsedData.type === AIAnswerQueryStreamType.AnswerPart ||
						parsedData.type === AIAnswerQueryStreamType.GenerationType ||
						parsedData.type === AIAnswerQueryStreamType.Trace ||
						parsedData.type === AIAnswerQueryStreamType.ErrorStateQuerySuggestions ||
						parsedData.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage
					) {
						yield parsedData;
					} else {
						finalResponse = parsedData;
						done = true;
					}
				}
				// Keep the last (potentially incomplete) line in the buffer
				buffer = lines[0];
			}

			if (finalResponse?.type !== AIAnswerQueryStreamType.FinalResponse) {
				// The stream ended without a valid FINAL_RESPONSE. This is not a
				// parse failure - it usually means an upstream error (e.g. Stargate
				// returning a non-2xx such as 404 FAILURE_CLIENT_TCS) short-circuited
				// the stream so the backend never emitted a final response body.
				// Capture non-UGC diagnostics (the type we actually received and, when
				// enabled, the trace id) so this failure is diagnosable if it recurs.
				const captureNoFinalResponseDetails = fg('kd_sain_capture_backend_error_details');
				return {
					state: 'failed',
					reason: 'no_final_response',
					error: 'Unexpected final response value',
					details: captureNoFinalResponseDetails
						? getNoFinalResponseDetails(finalResponse)
						: undefined,
					traceId: captureNoFinalResponseDetails ? getTraceIdFromResponse(response) : undefined,
				};
			}

			return { state: 'complete', value: finalResponse };
		} catch (parsingError: unknown) {
			return {
				state: 'failed',
				reason: 'parsing',
				...(fg('kd_sain_stream_error_diagnostics') && {
					diagnosticType:
						parsingError instanceof SyntaxError ? 'invalid_json' : 'stream_message_mapping_error',
					failureStage: parsingError instanceof SyntaxError ? 'frame_parse' : 'message_mapping',
					responseContentType: getResponseContentType(response),
					traceId: getTraceIdFromResponse(response),
				}),
				error: parsingError,
			};
		}
	} catch (error: unknown) {
		if (error instanceof DOMException && error.name === 'AbortError') {
			// this is likely an abort error
			return { state: 'failed', reason: 'aborted' };
		}

		if (error instanceof Error) {
			return {
				state: 'failed',
				reason: 'error',
				details: error.message,
				...(fg('kd_sain_stream_error_diagnostics') && {
					diagnosticType: 'unexpected_stream_error',
					failureStage: 'request',
				}),
				error,
			};
		}

		return { state: 'failed', reason: 'unknown' };
	}
}

const getAgentInput = ({
	variables,
	fetchConfig,
}: {
	variables: SearchAIAnswerQueryVariables | undefined;
	fetchConfig: FetchConfig | undefined;
}) => {
	const { query, locale, additional_context, experimentContext, isQuerySuggestion } =
		variables || {};
	return {
		content: query,
		locale,
		additional_context,
		entity_ari: fetchConfig?.entity_ari,
		...(experimentContext && fg('kd_sain_backend_exp_context') && { experimentContext }),
		...(typeof isQuerySuggestion === 'boolean' ? { isQuerySuggestion } : {}),
	};
};

const getPromptOverrideContext = () => {
	if (!fg('convo_ai_sain_enable_prompt_override_hello_only')) {
		return {};
	}

	const promptOverride = getSessionItem(PROMPT_OVERRIDE_SESSION_KEY);
	if (!promptOverride) {
		return {};
	}

	return {
		aiFeatureContext: {
			systemPromptOverride: promptOverride,
		},
	};
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export async function* sainStreamFetchConvoAIService({
	fetchReturn,
	variables,
	isReadingAids,
	fetchConfig,
	generatorConfig = {
		yieldAnswerPart: true,
		yieldFinalResponse: false,
	},
	storeMessage,
	onStreamFinalResponse,
}: AskSainFetchHandlerArgs & {
	isReadingAids: boolean;
	storeMessage?: { store_message?: boolean };
	generatorConfig?: {
		yieldAnswerPart?: boolean;
		yieldFinalResponse?: boolean;
	};
	onStreamFinalResponse?: () => void;
}): AIAnswerQueryStream {
	try {
		const { filters, selectedProduct } = variables || {};
		const { experience, named_id } = getAgentAndExperience(isReadingAids);

		const url = `${
			fetchConfig?.hostName || ''
		}${ASSISTANCE_SERVICE_API_BASE_URL}/rovo/v1/sain/stream`;

		const streamFilters: Record<string, unknown>[] =
			filters && !Array.isArray(filters) && fg('kd_add_common_filters_support_to_sain')
				? [
						{ commonFilters: filters.commonFilters },
						{ confluenceFilters: filters.confluenceFilters },
					]
				: [{ confluenceFilters: filters }];

		if (selectedProduct) {
			streamFilters.push({
				knowledgeSources: [selectedProduct],
			});
		}

		const response = await (fetchReturn
			? fetchReturn
			: fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Product': 'confluence',
						'X-Experience-Id': experience,
						'X-Rovo-Chat-Capabilities': JSON.stringify(['inline_lumina_enabled']),
						...((SAINFeatureControl.isSAINUpdatedFlowEnabled() ||
							SAINFeatureControl.isSAINLoadingStateTracesUpdateEnabled()) && {
							'X-Think-Deeper-Messages-Opt-In': 'true',
							'X-New-Tool-Traces-Opt-In': 'true',
						}),
						...(fetchConfig?.headers ? fetchConfig.headers : {}),
					},
					body: JSON.stringify({
						recipient_agent_named_id: named_id,
						agent_input: {
							...getAgentInput({ variables, fetchConfig }),
							filters: filters ? streamFilters : undefined,
							...getPromptOverrideContext(),
						},
						...storeMessage,
					}),
					credentials: fetchConfig?.credentials || 'same-origin', // fetch() uses 'same-origin' by default
					signal: fetchConfig?.timeout ? AbortSignal.timeout(fetchConfig?.timeout) : undefined,
				}));

		const traceId = fg('kd_sain_capture_backend_error_details')
			? (getTraceIdFromResponse(response) ?? null)
			: response.headers.get('x-trace-id') || null;
		if (!response.body) {
			return {
				state: 'failed',
				reason: 'backend',
				details: 'response.body missing',
				errorCode: response.status,
				backendFailureType: 'empty_body',
				traceId: traceId ?? undefined,
			};
		}

		try {
			if (!response.ok && fg('kd_http_error_status_propagation')) {
				return getNonOkResponseResult(response, traceId);
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder('utf-8');
			let buffer = '';
			let done = false;

			let finalResponse: AIAnswerQueryStreamMarkdownFinalPart['value'] | undefined;
			const pluginsInvoked: string[] = [];

			yield {
				type: AIAnswerQueryStreamType.AnswerType,
				message: {
					format: NLPSearchResultFormat.MARKDOWN,
				},
			};

			while (!done) {
				let readResult: ReadableStreamReadResult<Uint8Array>;
				try {
					readResult = await reader.read();
				} catch (error: unknown) {
					if (fg('kd_sain_stream_error_diagnostics')) {
						return {
							state: 'failed',
							reason: 'error',
							details: error instanceof Error ? error.message : 'Unable to read stream',
							diagnosticType: 'unexpected_stream_error',
							failureStage: 'stream_read',
							responseContentType: getResponseContentType(response),
							traceId: getTraceIdFromResponse(response),
							error: error instanceof Error ? error : new Error('Unable to read stream'),
						};
					}

					return { state: 'failed', reason: 'parsing', error };
				}
				const { value, done: doneReading } = readResult;

				done = doneReading;
				const chunkValue = decoder.decode(value);
				buffer = buffer + chunkValue;

				// Split the buffer by line breaks
				const lines = buffer.split('\n');

				// add case here to return error from stream
				if (!response.ok) {
					const finalResponse = JSON.parse(lines[0]);
					if (finalResponse?.errors.includes(NLPSearchErrorCodes.HIPAA_ENABLED_ERROR)) {
						return {
							state: 'failed',
							reason: 'backend',
							details: NLPSearchErrorCodes.HIPAA_ENABLED_ERROR,
							errorCode: response.status,
							backendFailureType: 'hipaa',
						};
					}
					// For non 200 status codes. When enabled, record structured, non-UGC
					// details (error key/status) plus trace id; otherwise fall back to a
					// generic string. Full body remains retrievable from logs via trace id.
					return fg('kd_sain_capture_backend_error_details')
						? {
								state: 'failed' as const,
								reason: 'backend' as const,
								details: extractStructuredErrorDetails(lines[0]) ?? DEFAULT_BACKEND_ERROR_DETAILS,
								errorCode: response.status,
								backendFailureType: 'http_error',
								traceId: traceId ?? undefined,
							}
						: {
								state: 'failed' as const,
								reason: 'backend' as const,
								details: DEFAULT_BACKEND_ERROR_DETAILS,
								errorCode: response.status,
								backendFailureType: 'http_error',
							};
				}

				// Process all complete lines, except for the last one (which might be incomplete)
				while (lines.length > 1) {
					const line = lines.shift()!;

					const parsedData = mapStreamSAINResponse(JSON.parse(line), traceId);

					if (parsedData.type === 'other') {
						continue;
					} else if (parsedData.type === 'plugin-invocation') {
						pluginsInvoked.push(parsedData.pluginName);
						continue;
					} else if (
						parsedData.type === AIAnswerQueryStreamType.AnswerType ||
						parsedData.type === AIAnswerQueryStreamType.AnswerPart ||
						parsedData.type === AIAnswerQueryStreamType.GenerationType ||
						parsedData.type === AIAnswerQueryStreamType.Trace ||
						parsedData.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage
					) {
						if (generatorConfig.yieldAnswerPart) {
							yield parsedData;
						}
					} else if (parsedData.type === 'follow-up') {
						if (!finalResponse?.message || !variables?.followUpsEnabled) {
							continue;
						}

						finalResponse.message.nlpFollowUpResults = {
							followUps: parsedData.queries,
						};

						const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
							abTestId: 'kd_sain_show_no_answer_suggestions_exp',
						});

						if (!serveErrorStateSuggestedQueriesExp) {
							done = true;
						}
					} else if (parsedData.type === AIAnswerQueryStreamType.ErrorStateQuerySuggestions) {
						if (!finalResponse?.message) {
							continue;
						}

						finalResponse.message.nlpSuggestedQueries = parsedData.suggested_queries;

						done = true;
					} else if (parsedData.type === AIAnswerQueryStreamType.EarlyStop) {
						return { state: 'complete', value: parsedData };
					} else {
						finalResponse = parsedData;
						onStreamFinalResponse?.();

						if (!variables?.followUpsEnabled) {
							done = true;
						}

						if (generatorConfig.yieldFinalResponse) {
							yield finalResponse;
						}
					}
				}
				// Keep the last (potentially incomplete) line in the buffer
				buffer = lines[0];
			}

			if (finalResponse?.type !== AIAnswerQueryStreamType.FinalResponse) {
				// The stream ended without a valid FINAL_RESPONSE. This is not a
				// parse failure - it usually means an upstream error (e.g. Stargate
				// returning a non-2xx such as 404 FAILURE_CLIENT_TCS) short-circuited
				// the stream so the backend never emitted a final response body.
				const captureNoFinalResponseDetails = fg('kd_sain_capture_backend_error_details');
				return {
					state: 'failed',
					reason: 'no_final_response',
					error: 'Unexpected final response value',
					details: captureNoFinalResponseDetails
						? getNoFinalResponseDetails(finalResponse)
						: undefined,
					traceId: captureNoFinalResponseDetails ? (traceId ?? undefined) : undefined,
				};
			}

			if (fg('kd_add_plugins_invoked')) {
				const filteredPluginsInvoked = pluginsInvoked.filter(Boolean);
				if (filteredPluginsInvoked.length > 0 && finalResponse.message) {
					const existing =
						(finalResponse.message.extraAPIAnalyticsAttributes?.pluginsInvoked as
							| string[]
							| undefined) ?? [];
					finalResponse.message.extraAPIAnalyticsAttributes = {
						...finalResponse.message.extraAPIAnalyticsAttributes,
						pluginsInvoked: [...new Set([...existing, ...filteredPluginsInvoked])],
					};
				}
			}

			return { state: 'complete', value: finalResponse };
		} catch (parsingError: unknown) {
			return {
				state: 'failed',
				reason: 'parsing',
				...(fg('kd_sain_stream_error_diagnostics') && {
					diagnosticType:
						parsingError instanceof SyntaxError ? 'invalid_json' : 'stream_message_mapping_error',
					failureStage: parsingError instanceof SyntaxError ? 'frame_parse' : 'message_mapping',
					responseContentType: getResponseContentType(response),
					traceId: getTraceIdFromResponse(response),
				}),
				error: parsingError,
			};
		}
	} catch (error: unknown) {
		if (error instanceof DOMException && error.name === 'AbortError') {
			return { state: 'failed', reason: 'aborted' };
		}

		if (error instanceof DOMException && error.name === 'TimeoutError') {
			return { state: 'failed', reason: 'timeout' };
		}

		if (error instanceof Error) {
			return {
				state: 'failed',
				reason: 'error',
				details: error.message,
				...(fg('kd_sain_stream_error_diagnostics') && {
					diagnosticType: 'unexpected_stream_error',
					failureStage: 'request',
				}),
				error,
			};
		}

		return { state: 'failed', reason: 'unknown' };
	}
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export async function sainRestFetchHandlerAssistanceService({
	variables,
	isReadingAids,
	fetchConfig,
}: AskSainFetchHandlerArgs & {
	isReadingAids: boolean;
}): Promise<SearchAIAnswerQueryType> {
	const { experience, named_id } = getAgentAndExperience(isReadingAids);

	const url = `${
		fetchConfig?.hostName || ''
	}${ASSISTANCE_SERVICE_API_BASE_URL}/chat/v1/invoke_agent`;

	const response = await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Product': 'confluence',
			'X-Experience-Id': experience,
			...fetchConfig?.headers,
		},
		body: JSON.stringify({
			recipient_agent_named_id: named_id,
			agent_input: getAgentInput({ variables, fetchConfig }),
		}),
		credentials: fetchConfig?.credentials || 'same-origin', // fetch() uses 'same-origin' by default
		signal: fetchConfig?.timeout ? AbortSignal.timeout(fetchConfig?.timeout) : undefined,
	});

	// When enabled, use the broadened multi-header trace-id extraction; otherwise
	// fall back to the original single-header behavior.
	const traceId = fg('kd_sain_capture_backend_error_details')
		? (getTraceIdFromResponse(response) ?? null)
		: response.headers.get('x-trace-id') || null;
	const data: AgentResponse | AgentError = await response.json();

	if ('message_template' in data) {
		const { errorState, networkErrorReason, networkErrorDetails, backendFailureType } =
			mapErrorToSain(data, traceId);
		return {
			nlpSearch: {
				errorState,
				networkErrorReason,
				networkErrorDetails,
				backendFailureType,
				disclaimer: null,
				format: NLPSearchResultFormat.MARKDOWN,
				uniqueSources: [],
			},
		};
	}

	return {
		nlpSearch: mapMessageToNLPSearchType(data, traceId),
	};
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export async function* aiDefinitionsStreamFetchConvoAIService({
	fetchReturn,
	variables,
	fetchConfig,
	generatorConfig = {
		yieldAnswerPart: true,
		yieldFinalResponse: false,
	},
	onStreamFinalResponse,
}: AskSainFetchHandlerArgs & {
	generatorConfig?: {
		yieldAnswerPart?: boolean;
		yieldFinalResponse?: boolean;
	};
	onStreamFinalResponse?: () => void;
}): AIAnswerQueryStream {
	try {
		const url = `${
			fetchConfig?.hostName || ''
		}${ASSISTANCE_SERVICE_API_BASE_URL}/rovo/v1/ai-definition`;

		const response = await (fetchReturn
			? fetchReturn
			: fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Product': 'confluence',
						'X-Experience-Id': 'reading-aids',
						...(fetchConfig?.headers ? fetchConfig.headers : {}),
					},
					body: JSON.stringify({
						...getAgentInput({ variables, fetchConfig }),
					}),
					credentials: fetchConfig?.credentials || 'same-origin',
					signal: fetchConfig?.timeout ? AbortSignal.timeout(fetchConfig?.timeout) : undefined,
				}));

		const traceId = fg('kd_sain_capture_backend_error_details')
			? (getTraceIdFromResponse(response) ?? null)
			: response.headers.get('x-trace-id') || null;
		if (!response.body) {
			return {
				state: 'failed',
				reason: 'backend',
				details: 'response.body missing',
				errorCode: response.status,
				backendFailureType: 'empty_body',
				traceId: traceId ?? undefined,
			};
		}

		try {
			if (!response.ok && fg('kd_http_error_status_propagation')) {
				return getNonOkResponseResult(response, traceId);
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder('utf-8');
			let buffer = '';
			let done = false;

			let finalResponse: AIAnswerQueryStreamMarkdownFinalPart['value'] | undefined;

			yield {
				type: AIAnswerQueryStreamType.AnswerType,
				message: {
					format: NLPSearchResultFormat.MARKDOWN,
				},
			};

			while (!done) {
				const { value, done: doneReading } = await reader.read();

				done = doneReading;
				const chunkValue = decoder.decode(value);
				buffer = buffer + chunkValue;

				// Split the buffer by line breaks
				const lines = buffer.split('\n');

				// add case here to return error from stream
				if (!response.ok) {
					const finalResponse = JSON.parse(lines[0]);
					if (finalResponse?.errors.includes(NLPSearchErrorCodes.HIPAA_ENABLED_ERROR)) {
						return {
							state: 'failed',
							reason: 'backend',
							details: NLPSearchErrorCodes.HIPAA_ENABLED_ERROR,
							errorCode: response.status,
							backendFailureType: 'hipaa',
						};
					}
					// For non 200 status codes. When enabled, record structured, non-UGC
					// details (error key/status) plus trace id; otherwise fall back to a
					// generic string. Full body remains retrievable from logs via trace id.
					return fg('kd_sain_capture_backend_error_details')
						? {
								state: 'failed' as const,
								reason: 'backend' as const,
								details: extractStructuredErrorDetails(lines[0]) ?? DEFAULT_BACKEND_ERROR_DETAILS,
								errorCode: response.status,
								backendFailureType: 'http_error',
								traceId: traceId ?? undefined,
							}
						: {
								state: 'failed' as const,
								reason: 'backend' as const,
								details: DEFAULT_BACKEND_ERROR_DETAILS,
								errorCode: response.status,
								backendFailureType: 'http_error',
							};
				}

				// Process all complete lines, except for the last one (which might be incomplete)
				while (lines.length > 1) {
					const line = lines.shift()!;

					const parsedData = mapStreamDefinitionResponse(JSON.parse(line), traceId);

					if (parsedData.type === 'other') {
						continue;
					} else if (
						parsedData.type === AIAnswerQueryStreamType.AnswerType ||
						parsedData.type === AIAnswerQueryStreamType.AnswerPart ||
						parsedData.type === AIAnswerQueryStreamType.GenerationType ||
						parsedData.type === AIAnswerQueryStreamType.Trace ||
						parsedData.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage
					) {
						if (generatorConfig.yieldAnswerPart) {
							yield parsedData;
						}
					} else if (parsedData.type === 'follow-up') {
						if (!finalResponse?.message || !variables?.followUpsEnabled) {
							continue;
						}

						finalResponse.message.nlpFollowUpResults = {
							followUps: parsedData.queries,
						};

						const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
							abTestId: 'kd_sain_show_no_answer_suggestions_exp',
						});

						if (!serveErrorStateSuggestedQueriesExp) {
							done = true;
						}
					} else if (parsedData.type === AIAnswerQueryStreamType.ErrorStateQuerySuggestions) {
						if (!finalResponse?.message) {
							continue;
						}

						finalResponse.message.nlpSuggestedQueries = parsedData.suggested_queries;

						done = true;
					} else {
						finalResponse = parsedData;

						onStreamFinalResponse?.();

						if (!variables?.followUpsEnabled) {
							done = true;
						}

						if (generatorConfig.yieldFinalResponse) {
							yield finalResponse;
						}
					}
				}
				// Keep the last (potentially incomplete) line in the buffer
				buffer = lines[0];
			}

			if (finalResponse?.type !== AIAnswerQueryStreamType.FinalResponse) {
				// The stream ended without a valid FINAL_RESPONSE. This is not a
				// parse failure - it usually means an upstream error (e.g. Stargate
				// returning a non-2xx such as 404 FAILURE_CLIENT_TCS) short-circuited
				// the stream so the backend never emitted a final response body.
				const captureNoFinalResponseDetails = fg('kd_sain_capture_backend_error_details');
				return {
					state: 'failed',
					reason: 'no_final_response',
					error: 'Unexpected final response value',
					details: captureNoFinalResponseDetails
						? getNoFinalResponseDetails(finalResponse)
						: undefined,
					traceId: captureNoFinalResponseDetails ? (traceId ?? undefined) : undefined,
				};
			}

			return { state: 'complete', value: finalResponse };
		} catch (parsingError: unknown) {
			return { state: 'failed', reason: 'parsing', error: parsingError };
		}
	} catch (error: unknown) {
		if (error instanceof DOMException && error.name === 'AbortError') {
			return { state: 'failed', reason: 'aborted' };
		}

		if (error instanceof DOMException && error.name === 'TimeoutError') {
			return { state: 'failed', reason: 'timeout' };
		}

		if (error instanceof Error) {
			return { state: 'failed', reason: 'error', details: error.message, error };
		}

		return { state: 'failed', reason: 'unknown' };
	}
}
